import { z } from "zod";

export const workspaceItemKindSchema = z.enum([
	"CARD",
	"PREPRINT",
	"DRAFT",
	"WHITEBOARD",
	"SHAPE",
	"PERSON",
	"ASSET",
]);
export type WorkspaceItemKind = z.infer<typeof workspaceItemKindSchema>;

export const workspaceSearchHitSchema = z.object({
	id: z.string(),
	key: z.string().nullable().optional(),
	kind: z.string(),
	name: z.string(),
	snippet: z.string().nullable().optional(),
	ownerId: z.string().nullable().optional(),
	ownerName: z.string().nullable().optional(),
	parentId: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
});
export type WorkspaceSearchHit = z.infer<typeof workspaceSearchHitSchema>;

export const workspaceItemSchema = z.object({
	id: z.string(),
	key: z.string().nullable().optional(),
	kind: z.string(),
	name: z.string(),
	markdown: z.string().default(""),
	llmContent: z.string().default(""),
	referencedIds: z.array(z.string()).default([]),
	ownerId: z.string().nullable().optional(),
	parentId: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
});
export type WorkspaceItem = z.infer<typeof workspaceItemSchema>;

function safeJsonParse(input: unknown): unknown {
	if (typeof input !== "string") {
		return input;
	}
	try {
		return JSON.parse(input);
	} catch {
		return input;
	}
}

export function parseWorkspaceSearchHits(raw: unknown): WorkspaceSearchHit[] {
	const parsed = safeJsonParse(raw);
	if (!parsed || typeof parsed !== "object") {
		return [];
	}

	const candidate =
		(parsed as { data?: { workspace?: { search?: unknown } } })?.data?.workspace
			?.search ??
		(parsed as { workspace?: { search?: unknown } })?.workspace?.search ??
		(parsed as { search?: unknown })?.search ??
		(Array.isArray(parsed) ? parsed : null);

	if (!Array.isArray(candidate)) {
		return [];
	}

	const results: WorkspaceSearchHit[] = [];
	for (const item of candidate) {
		const result = workspaceSearchHitSchema.safeParse(item);
		if (result.success) {
			results.push(result.data);
		}
	}
	return results;
}

export function parseWorkspaceItems(raw: unknown): WorkspaceItem[] {
	const parsed = safeJsonParse(raw);
	if (!parsed || typeof parsed !== "object") {
		return [];
	}

	const candidate =
		(parsed as { data?: { workspace?: { get?: unknown } } })?.data?.workspace
			?.get ??
		(parsed as { workspace?: { get?: unknown } })?.workspace?.get ??
		(parsed as { get?: unknown })?.get ??
		(parsed as { items?: unknown })?.items ??
		(parsed as { data?: { items?: unknown } })?.data?.items ??
		(Array.isArray(parsed) ? parsed : null);

	if (!Array.isArray(candidate)) {
		return [];
	}

	const results: WorkspaceItem[] = [];
	for (const item of candidate) {
		const result = workspaceItemSchema.safeParse(item);
		if (result.success) {
			results.push(result.data);
		}
	}
	return results;
}
